// client/src/utils/cookieConsent.js
// UK/UK GDPR-compliant cookie consent helpers.
// Consent is stored in localStorage; no cookies are set for consent itself
// to avoid needing consent for the consent mechanism.

const STORAGE_KEY = "academyCostumesCookieConsent";
const CONSENT_VERSION = 1;

export const CONSENT_CATEGORIES = Object.freeze({
  essential: "essential",
  analytics: "analytics",
  marketing: "marketing",
  preferences: "preferences",
});

export const CATEGORY_LABELS = Object.freeze({
  essential: "Essential",
  analytics: "Analytics",
  marketing: "Marketing",
  preferences: "Preferences",
});

export const CATEGORY_DESCRIPTIONS = Object.freeze({
  essential:
    "Required for the site to function. Includes session management, security tokens, and cart persistence. Cannot be disabled.",
  analytics:
    "Help us understand how visitors use the site so we can improve it. No personal data is shared with third parties.",
  marketing:
    "Allow us to show relevant promotions and measure campaign effectiveness. No tracking is active on this site by default.",
  preferences:
    "Remember your settings such as language, currency, or display preferences across visits.",
});

/** @returns {Record<string, boolean>} */
function defaultConsent() {
  return {
    [CONSENT_CATEGORIES.essential]: true,   // always on, immutable
    [CONSENT_CATEGORIES.analytics]: false,
    [CONSENT_CATEGORIES.marketing]: false,
    [CONSENT_CATEGORIES.preferences]: false,
  };
}

/** @returns {{ version: number, decided: boolean, categories: Record<string, boolean> } | null} */
export function readConsent() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.decided !== "boolean") return null;

    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, boolean>} categories
 * @param {boolean} decided
 */
export function writeConsent(categories, decided = true) {
  if (typeof window === "undefined") return;

  const safe = {
    ...defaultConsent(),
    ...categories,
    // Essential is always true regardless
    [CONSENT_CATEGORIES.essential]: true,
  };

  const payload = {
    version: CONSENT_VERSION,
    decided,
    categories: safe,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be blocked in private browsing — fail silently
  }
}

/** Accept all categories */
export function acceptAllConsent() {
  const all = {};
  Object.values(CONSENT_CATEGORIES).forEach((key) => {
    all[key] = true;
  });
  writeConsent(all, true);
}

/** Accept essential only, reject all others */
export function rejectNonEssentialConsent() {
  writeConsent(defaultConsent(), true);
}

/**
 * Returns true if consent has been given (decided = true) for a specific category.
 * Essential always returns true.
 * @param {string} category
 */
export function hasConsent(category) {
  if (category === CONSENT_CATEGORIES.essential) return true;

  const consent = readConsent();
  if (!consent || !consent.decided) return false;

  return Boolean(consent.categories?.[category]);
}

/** Returns true if the user has not yet made a consent decision */
export function needsConsentDecision() {
  const consent = readConsent();
  return !consent || consent.decided !== true;
}
